import React, { useCallback, useEffect } from 'react';
import { useFormikContext, FormikProps } from 'formik';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';

// actions
import { setShouldCloseSection, setSelectedSectionIndex, setShouldClosePanel } from 'ducks/merchantDucks/items/actions';

// types
import RootState from 'ducks/RootState';

// hooks
import useOnMount from 'hooks/useOnMount';
import useSharedBoolean from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedBoolean';

// components
import { FormikEffect } from 'components/FormikWrapper';

export enum SectionType {
  Preview,
  Templates,
  PriceOptions,
  Content,
  ContentType,
  Restrictions,
  Metadata,
  EventBriteEvent,
  GiftDetails,
  Donations,
  LiveLike,
}

export enum PackagesSectionType {
  Preview,
  PriceOptions,
  ManageAssets,
  Metadata,
}

export enum VouchersSectionType {
  Details,
}

export enum CodeOnlySectionType {
  Preview,
  Templates,
  CodeOptions,
  Content,
  Restrictions,
  Metadata,
  EventBriteEvent,
}

type AccordionSectionType = SectionType | PackagesSectionType | VouchersSectionType | CodeOnlySectionType;

interface HelperProps {
  index: AccordionSectionType;
  closePanel: Function;
}

interface AccordionHelperProps extends HelperProps {
  submitForm: Function;
}

interface FormAccordionHelperProps extends HelperProps {
  disableFormikEffect?: boolean;
  initialValues: any;
}

export const AccordionHelper = ({ submitForm, index, closePanel }: AccordionHelperProps) => {
  const dispatch = useDispatch();

  const selectedSectionIndex = useSelector((state: RootState) => state.items.itemDetails.selectedSectionIndex);
  const shouldClosePanel = useSelector((state: RootState) => state.items.itemDetails.shouldClosePanel);

  useEffect(() => {
    if (selectedSectionIndex === index) {
      submitForm();
      dispatch(setSelectedSectionIndex(-1));
    }
  }, [selectedSectionIndex]);

  useEffect(() => {
    if (shouldClosePanel) {
      closePanel();
      dispatch(setShouldClosePanel(false));
      dispatch(setShouldCloseSection(true));
    }
  }, [shouldClosePanel]);

  return null;
};

const FormAccordionHelper = ({ initialValues, index, closePanel, disableFormikEffect }: FormAccordionHelperProps) => {
  const { values, submitForm } = useFormikContext();

  const dispatch = useDispatch();

  const [, setSaveButtonDisabled] = useSharedBoolean('isSaveButtonDisabled', false);

  const shouldCloseSection = useSelector((state: RootState) => state.items.itemDetails.shouldCloseSection);

  const checkForDirtyValues = useCallback(() => {
    if (isEqual(values, initialValues) && !shouldCloseSection) {
      dispatch(setShouldCloseSection(true));
    } else if (!isEqual(values, initialValues) && shouldCloseSection) {
      dispatch(setShouldCloseSection(false));
    }
  }, [values, submitForm, shouldCloseSection]);

  useEffect(checkForDirtyValues, [checkForDirtyValues]);

  useOnMount(() => checkForDirtyValues);

  const handleStatusChange = ({ isSubmitting, dirty }: FormikProps<any>) => {
    setSaveButtonDisabled(isSubmitting || !dirty);
  };

  return (
    <>
      {!disableFormikEffect && <FormikEffect onStatusChange={handleStatusChange} />}
      <AccordionHelper index={index} closePanel={closePanel} submitForm={submitForm} />
    </>
  );
};

export default FormAccordionHelper;
